import { capitalize } from '@/lib/collections';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Swal, { type SweetAlertIcon } from 'sweetalert2';

import { PictoVoirDetail } from '@/assets/icons-april-fr/pictos';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { HOST_MB } from '@/lib/constants';
import { useTerminateAssuranceTransitoire } from '@/hooks/api/use-contract-actions';
import { useContractsData } from '@/hooks/api/use-contracts';
import { useCurrentUser, useIsFleetUser } from '@/hooks/api/use-user';
import useIsPWA from '@/hooks/use-is-pwa';
import { formatDate, toDate } from '@/lib/dates';
import type { Contract } from '@/types/api/contract';
import getProductIcon from '@/lib/product-icon';

import PromoBlocs from '@/pages/home/promo-blocs';

const getStatusVariant = (statutCode?: string) => {
    switch (statutCode) {
        case '1':
            return 'success' as const;
        case '6':
        case '5':
            return 'destructive' as const;
        case 'I':
        case '2':
            return 'warning' as const;
        default:
            return 'secondary' as const;
    }
};

const ListeContracts = () => {
    const contracts = useContractsData();
    const isEligibleParrainage = useCurrentUser().eligible_parrainage;
    const { mutate: postTerminateAssuranceTransitoire } = useTerminateAssuranceTransitoire();
    const isPWA = useIsPWA();
    const isFleetUser = useIsFleetUser();

    const handleClickInfoAssTransitoire = () => {
        Swal.fire({
            icon: '' as unknown as SweetAlertIcon,
            title: '',
            text: 'Ce véhicule bénéficie des mêmes garanties et options que celles de votre nouveau véhicule, uniquement s\'il est mis en circulation pour des essais en vue de la vente et pour une durée maximale de 30 jours à compter du jour où l\'assurance a été reportée sur le nouveau véhicule.',
            showConfirmButton: true,
            confirmButtonText: 'Ok',
            showCloseButton: true
        });
    };

    const handleTerminateAssuranceTransitoire = (contractId: number) => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Confirmez-vous avoir vendu votre ancien véhicule ? Cela mettra fin à l\'assurance transitoire.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Oui, je confirme',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                postTerminateAssuranceTransitoire({ contractId });

                Swal.fire(
                    'Terminé !',
                    'Votre ancien véhicule n\'est plus couvert par l\'assurance transitoire.',
                    'success'
                );
            }
        });
    };

    const hasActiveTransitoire = (contract: Contract) => {
        return (
            contract.old_vehicule?.immatriculation &&
            !contract.vehicule?.vente_old_vehicule &&
            toDate(contract.old_vehicule.date_fin_piece).getTime() > Date.now()
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="space-y-2 sm:space-y-3 mt-4">
                <h1 className="text-xl sm:text-2xl font-bold">Mes contrats</h1>
            </div>

            {/* Contracts Grid */}
            <div data-tutorial="contracts-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {contracts.map((c, index) => {
                    const ProductIcon = getProductIcon(c.vehicule?.codeProduit);

                    return (
                        <Card key={c.id} className="transition-all duration-200 hover:shadow-lg flex flex-col overflow-hidden">
                            <CardHeader className="overflow-hidden">
                                <div className="flex flex-wrap items-start justify-between gap-2 overflow-hidden">
                                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 shrink-0">
                                            <ProductIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className="font-semibold text-sm sm:text-base break-words">Contrat N°{c.numeroPolice}</h2>
                                            {c.vehicule?.marque && (
                                                <p className="text-xs sm:text-sm text-muted-foreground break-words">
                                                    {c.vehicule.marque} {c.vehicule.modele}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <Badge variant={getStatusVariant(c.situation?.statutCode)} className={isPWA ? "shrink-0 text-sm px-4 py-1.5 whitespace-nowrap" : "shrink-0 text-[10px] sm:text-xs whitespace-nowrap"}>
                                        {capitalize(c.situation?.statut)}
                                    </Badge>
                                </div>
                            </CardHeader>

                            <CardContent className="flex-1">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                    <div>
                                        <p className="text-muted-foreground">Date d'effet</p>
                                        <p className="font-medium">{formatDate(c.situation?.dateAffaireNouvelle)}</p>
                                    </div>
                                    {c.vehicule?.immatriculation && (
                                        <div>
                                            <p className="text-muted-foreground">Immatriculation</p>
                                            <span className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded-md font-medium">
                                                {c.vehicule.immatriculation}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Assurance transitoire */}
                                {hasActiveTransitoire(c) && (
                                    <div className="mt-6 pt-6 border-t">
                                        <div className="flex items-center gap-2 mb-4">
                                            <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
                                            <h3 className="font-semibold text-sm">Assurance transitoire</h3>
                                            <button
                                                onClick={(e) => { e.preventDefault(); handleClickInfoAssTransitoire(); }}
                                                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-sm cursor-pointer"
                                                aria-label="Informations sur l'assurance transitoire"
                                            >
                                                ?
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                            <div>
                                                <p className="text-muted-foreground">Véhicule</p>
                                                <p className="font-medium">{c.old_vehicule?.marque} {c.old_vehicule?.modele}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Immatriculation</p>
                                                <span className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded-md font-medium">
                                                    {c.old_vehicule?.immatriculation}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Date d'effet</p>
                                                <p className="font-medium">{formatDate(c.old_vehicule?.date_effet_piece)}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Date de fin</p>
                                                <p className="font-medium">{formatDate(c.old_vehicule?.date_fin_piece)}</p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="mt-4 cursor-pointer"
                                            onClick={() => handleTerminateAssuranceTransitoire(c.id)}
                                        >
                                            Oui, j'ai vendu ce véhicule
                                        </Button>
                                    </div>
                                )}
                            </CardContent>

                            {/* Voir le détail */}
                            <div className="px-6 py-6 border-t mt-auto">
                                <Button asChild variant="outline" className="w-full cursor-pointer">
                                    <Link to={`/mes-contrats/mon-contrat/${c.id}`} {...(index === 0 ? { 'data-tutorial': 'contract-voir-detail' } : {})}>
                                        <PictoVoirDetail className="w-4 h-4" aria-hidden="true" />
                                        Voir le détail
                                    </Link>
                                </Button>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {HOST_MB === 43397 && !isFleetUser && <PromoBlocs isEligibleParrainage={isEligibleParrainage} />}
        </div>
    );
};

export default ListeContracts;
